import { useState, useRef, useEffect } from "react";
import "../styles/category.css";
import { Category } from "../types/Category";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import BaseApi from "../services/base-api";
import LoadingComponent from "./LoadingComponent";

const SubCategoryComponent = ({ categoryId }: { categoryId?: string }) => {
  const navigate = useNavigate();
  const [subCategories, setSubCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { t, i18n } = useTranslation();

  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update items per view based on screen size
  useEffect(() => {
    let isMounted = true;

    const loadSubCategories = async () => {
      if (!categoryId) {
        if (isMounted) {
          setIsLoading(false);
          setSubCategories([]);
          setSelectedCategory("All");
          setCurrentIndex(0);
        }
        return;
      }

      if (isMounted) {
        setIsLoading(true);
      }

      try {
        const allCategories = await getCategoriesById(categoryId);
        if (!isMounted) return;

        setSubCategories(allCategories || []);
        setSelectedCategory("All");
        setCurrentIndex(0);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSubCategories();

    const updateItemsPerView = () => {
      if (window.innerWidth < 640) {
        setItemsPerView(3); // Mobile: 3 item
      } else if (window.innerWidth < 1024) {
        setItemsPerView(3); // Tablet: 3 items
      } else {
        setItemsPerView(3); // Desktop: 3 items
      }
      setCurrentIndex(0); // Reset to first item on resize
    };

    updateItemsPerView();
    window.addEventListener("resize", updateItemsPerView);

    return () => {
      isMounted = false;
      window.removeEventListener("resize", updateItemsPerView);
    };
  }, [categoryId]);

  const getCategoriesById = async (id: string) => {
    const res = await BaseApi.get(`/categories/${id}`);
    // console.log("Category by ID response ----> ", res);
    if (res.success && res?.data) return res?.data?.subcategories;
    return [];
  };

  const checkSubCategory = async (id: string) => {
    const res = await BaseApi.get(`/categories/${id}`);
    // console.log("Category by ID response ----> ", res);
    if (res.success && res?.data?.subcategories?.length) return true;
    else return false;
  };

  const handleCategoryClick = async (category: Category) => {
    setIsLoading(true);
    setSelectedCategory(category?.name?.en || "");

    if (category?.subcategories?.length) {
      navigate(`/category/${category.id}`);
      return;
    }

    try {
      const hasSubCategory = await checkSubCategory(category.id);
      if (hasSubCategory) {
        navigate(`/category/${category.id}`);
      } else {
        navigate(`/products?categoryId=${category.id}`);
      }
    } catch (error) {
      console.error("Failed to check category details:", error);
      setIsLoading(false);
    }
  };

  const scrollToIndex = (index: number) => {
    if (!scrollRef.current || isTransitioning) return;

    setIsTransitioning(true);
    const container = scrollRef.current;

    // Get the width of one item including gap
    const items = container.children;
    if (items.length === 0) return;

    const firstItem = items[0] as HTMLElement;
    const itemWidth = firstItem.offsetWidth;
    const gap = itemsPerView === 1 ? 16 : 24;
    const scrollDistance = (itemWidth + gap) * index;

    container.scrollTo({
      left: scrollDistance,
      behavior: "smooth",
    });

    setCurrentIndex(index);

    // Reset transition flag after animation
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };

  const scrollLeft = () => {
    if (isTransitioning) return;
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToIndex(newIndex);
  };

  const scrollRight = () => {
    if (isTransitioning) return;
    const maxIndex = Math.max(0, (subCategories?.length || 0) - itemsPerView);
    const newIndex = Math.min(maxIndex, currentIndex + 1);
    scrollToIndex(newIndex);
  };

  const canScrollLeft = currentIndex > 0;
  const canScrollRight =
    currentIndex < (subCategories?.length || 0) - itemsPerView;

  // Calculate gap based on screen size
  const getGap = () => {
    if (typeof window === "undefined") return 24;
    return window.innerWidth < 640 ? 10 : 24;
  };

  if (isLoading) {
    return <LoadingComponent title="Categories" message="fetching-data" />;
  }

  return (
    <div className="w-full py-8">
      <div className="relative mx-auto px-2" ref={containerRef}>
        {/* Left Arrow */}
        {canScrollLeft && (
          <button
            onClick={scrollLeft}
            disabled={isTransitioning}
            className="
              absolute left-0 top-1/2 -translate-y-1/2
              bg-white hover:bg-gray-50 text-gray-800
              rounded-full
              p-2 sm:p-3
              transition-all duration-300 shadow-lg hover:scale-110 z-10
              border border-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
            "
            aria-label="Previous categories"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        )}

        {/* Categories Container */}
        <div className="overflow-hidden lg:px-10">
          <div
            ref={scrollRef}
            className="category-scroll-wrapper"
            style={{
              display: "flex",
              gap: `${getGap()}px`,
              overflowX: "hidden",
              scrollBehavior: "smooth",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {(subCategories || []).map((category: Category, index: number) => (
              <div
                key={category.id}
                className="category-item"
                style={{
                  flex: `0 0 calc((100% - ${(itemsPerView - 1) * getGap()}px) / ${itemsPerView})`,
                  maxWidth: `calc((100% - ${(itemsPerView - 1) * getGap()}px) / ${itemsPerView})`,
                  minWidth: "120px",
                }}
              >
                <button
                  onClick={() => handleCategoryClick(category)}
                  className={`
                    w-full
                    rounded-2xl
                    overflow-hidden
                    transition-all duration-300
                    hover:shadow-xl
                    ${
                      selectedCategory === category.name.en
                        ? "ring-4 ring-blue-500 shadow-2xl"
                        : "ring-2 ring-gray-200 hover:ring-gray-300 shadow-lg"
                    }
                  `}
                >
                  <div
                    className="relative bg-gradient-to-br from-gray-50 to-gray-100 transform hover:scale-105 transition-transform duration-300"
                    style={{
                      aspectRatio: "3/2",
                      width: "100%",
                    }}
                  >
                    <img
                      src={category.image}
                      alt={category.name.en}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />

                    {/* Selected Indicator */}
                    {selectedCategory === category.name.en && (
                      <div className="absolute top-2 right-2 bg-[#0d223f] text-white rounded-full p-1.5">
                        <svg
                          className="w-3 h-3 sm:w-4 sm:h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Category Name Below Image */}
                  <div className="mt-2 text-center">
                    <h3 className="text-gray-900 font-semibold text-sm sm:text-sm md:text-base lg:text-lg">
                      {(i18n.language === "ar"
                        ? category.name?.ar
                        : category.name?.en) || t("No sub category available")}
                    </h3>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Right Arrow */}
        {canScrollRight && (
          <button
            onClick={scrollRight}
            disabled={isTransitioning}
            className="
              absolute right-0 top-1/2 -translate-y-1/2
              bg-white hover:bg-gray-50 text-gray-800
              rounded-full
              p-2 sm:p-3
              transition-all duration-300 shadow-lg hover:scale-110 z-10
              border border-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
            "
            aria-label="Next categories"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        )}

        {/* Pagination Dots */}
        {subCategories && subCategories.length > itemsPerView && (
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({
              length: Math.ceil((subCategories?.length || 0) / itemsPerView),
            }).map((_, index) => {
              const isActive =
                Math.floor(currentIndex / itemsPerView) === index;
              return (
                <button
                  key={index}
                  onClick={() => scrollToIndex(index * itemsPerView)}
                  disabled={isTransitioning}
                  className={`
                    h-2 rounded-full transition-all duration-300
                    disabled:cursor-not-allowed
                    ${
                      isActive
                        ? "w-8 bg-[#0d223f]"
                        : "w-2 bg-gray-600 hover:bg-gray-800"
                    }
                  `}
                  aria-label={`Go to page ${index + 1}`}
                  aria-current={isActive ? "true" : "false"}
                />
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        /* Hide scrollbar */
        .category-scroll-wrapper::-webkit-scrollbar {
          display: none;
        }

        .category-scroll-wrapper {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        /* Prevent text selection during scroll */
        .category-scroll-wrapper {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }

        /* Ensure crisp images */
        .category-item img {
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
        }

        /* Smooth transitions */
        .category-item {
          transition: transform 0.3s ease;
        }

        /* Prevent layout shift */
        .category-scroll-wrapper > * {
          will-change: transform;
        }
      `}</style>
    </div>
  );
};

export default SubCategoryComponent;
